'use client';

import React from 'react';
import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { isEmpty, resolveSetting } from '../../../utils/settingsHelper';
import {
	containerWidthOptions,
	horizontalPaddingOptions,
	spacingOptions,
	verticalMarginOptions,
} from '../../editor/Toolbar/options';

const ReactPlayer = dynamic(() => import('react-player/lazy'), { ssr: false });

export const isEmbeddableVideoUrl = (url) => /(?:youtube\.com|youtu\.be|vimeo\.com)/.test(url || '');

export const getBgImageStyle = (src, position, size) => (src ? {
	background: `url(${src})  ${position || 'center'} / ${size || 'cover'} no-repeat`,
} : '');

export const getPageSectionClasses = (props) => classNames(
	verticalMarginOptions[props.verticalMargin],
	spacingOptions[props.spacing],
	horizontalPaddingOptions[props.horizontalPadding],
);

export const getPageSectionStyle = (props, settings) => {
	const {
		justifyContent,
		textAlign,
		background,
		background2,
		background3,
		bgGradientDirection,
		color,
	} = props;

	const bgProps = { background: '' };

	let bgColor;
	if (isEmpty(background2)) {
		bgColor = resolveSetting(background, settings);
	} else {
		const colors = [
			resolveSetting(background, settings),
			resolveSetting(background2, settings),
		];
		if (!isEmpty(background3)) {
			colors.push(resolveSetting(background3, settings));
		}
		bgColor = `linear-gradient(${bgGradientDirection || 'to right'}, ${colors.join(', ')})`;
	}
	bgProps.background += bgProps.background ? `, ${bgColor}` : bgColor;

	return {
		justifyContent,
		textAlign,
		...bgProps,
		color: resolveSetting(color, settings),
	};
};
export function PageSection(props) {
	const {
		children,
		settings,
		maxWidth,
		innerMaxWidth,
		backgroundImage,
		backgroundPosition,
		backgroundSize,
		mobileBackgroundImage,
		mobileBackgroundPosition,
		mobileBackgroundSize,
		isFooter,
		customId,
		videoUrl,
		mobileVideoUrl,
	} = props;

	const Tag = isFooter ? 'footer' : 'section';
	const hasVideo = videoUrl || mobileVideoUrl;

	if (hasVideo) {
		return (
			<Tag
				className="relative w-full overflow-hidden scroll-mt-24"
				style={getPageSectionStyle(props, settings)}
				id={customId || props.id}
			>
				{videoUrl && (
					isEmbeddableVideoUrl(videoUrl) ? (
						<div className="hidden sm:block w-full h-full [&>div]:!absolute [&>div]:inset-0" style={{ position: 'relative', paddingTop: '56.25%' }}>
							<ReactPlayer url={videoUrl} playing muted loop width="100%" height="100%" controls={false} />
						</div>
					) : (
						<video
							className="hidden sm:block w-full h-full object-cover"
							autoPlay
							muted
							loop
							playsInline
						>
							<source src={videoUrl} />
						</video>
					)
				)}
				{mobileVideoUrl && (
					isEmbeddableVideoUrl(mobileVideoUrl) ? (
						<div className="block sm:hidden w-full h-full [&>div]:!absolute [&>div]:inset-0" style={{ position: 'relative', paddingTop: '56.25%' }}>
							<ReactPlayer url={mobileVideoUrl} playing muted loop width="100%" height="100%" controls={false} />
						</div>
					) : (
						<video
							className="block sm:hidden w-full h-full object-cover"
							autoPlay
							muted
							loop
							playsInline
						>
							<source src={mobileVideoUrl} />
						</video>
					)
				)}
			</Tag>
		);
	}

	return (
		<Tag
			className="flex w-full scroll-mt-24"
			style={getPageSectionStyle(props, settings)}
			id={customId || props.id}
		>
			<style jsx>
				{`
					@media (min-width: 641px) {
						.background {
							background: ${getBgImageStyle(backgroundImage, backgroundPosition, backgroundSize).background}
						}
					}

					@media (max-width: 640px) {
						.background {
							background: ${getBgImageStyle(mobileBackgroundImage, mobileBackgroundPosition, mobileBackgroundSize).background}
						}
					}
				`}
			</style>
			<div
				className="w-full mx-auto page-section-inner background"
				style={{ maxWidth: containerWidthOptions[maxWidth] }}
			>
				<div
					className={classNames('mx-auto', getPageSectionClasses(props))}
					 style={{ maxWidth: innerMaxWidth ? containerWidthOptions[innerMaxWidth] : null }}
				>
					{children}
				</div>
			</div>
		</Tag>
	);
}
